import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import { useToast } from './ui/use-toast';
import { FUNCTION_NAMES } from '../constants';
import { API_BASE_URL } from '../config';
import { fetchWithRetry, getTourTypeHebrew } from '../utils/tourUtils';
import EditTourModal from './EditTourModal';
import { Calendar, Copy, ExternalLink, Pencil, Edit } from 'lucide-react';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { useNavigate } from 'react-router-dom';
import { useUser } from './UserContext';


const PublicTourLinkManager = ({
  tourId,
  showToggle = true,
  showEditButton = true,
  showEditorButton = true,
  showCopyButton = true,
  labelOptions = { active: 'Active', inactive: 'Inactive' },
}) => {
  const [tours, setTours] = useState([]);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedTourForEdit, setSelectedTourForEdit] = useState(null);
  const { toast } = useToast();
  const navigate = useNavigate();
  const { selectedUser: globalSelectedUser, hasPermission } = useUser(); // still access global if needed

  const isAdmin = hasPermission('ADMIN_USER');
  const isFullEditor = hasPermission('FULL_EDITOR');
  const canEdit = isAdmin || isFullEditor;

  useEffect(() => {
    if (tourId) fetchReleatedTours();
  }, [tourId]);

  const fetchReleatedTours = async () => {
    try {
      // First: fetch the tour itself
      const baseTour = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'tour.findUnique',
          payload: { id: tourId },
        },
        label: 'fetch base tour',
      });

      if (!baseTour) throw new Error('Base tour not found');

      // Second: fetch ALL tours matching the same project/lot/building/floor/space
      const relatedTours = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'tour.findMany',
          payload: {
            data: {
              projectId: baseTour.projectId,
              lotId: baseTour.lotId,
              buildingId: baseTour.buildingId,
              floorId: baseTour.floorId,
              spaceId: baseTour.spaceId,
            },
          },
        },
        label: 'fetch related tours',
      });

      if (!relatedTours || relatedTours.length === 0) {
        throw new Error('No related tours found');
      }

      setTours(relatedTours);
    } catch (error) {
      console.error('Error fetching tour status:', error);
      toast({
        title: 'Error',
        description: 'Failed to fetch tour public status',
        variant: 'destructive',
      });
    }
  };

  const togglePublicStatus = async tour => {
    try {
      const updated = await fetchWithRetry({
        url: `${API_BASE_URL}/api/post`,
        data: {
          function_name: FUNCTION_NAMES.PRISMA,
          operation: 'tour.update',
          payload: {
            id: tour.id,
            data: { ...tour, isActivePublic: !tour.isActivePublic },
          },
        },
        label: 'toggle tour status',
      });

      if (!updated) throw new Error('Failed to update');
      setTours(prev => prev.map(t => (t.id === tour.id ? updated : t)));
      toast({
        title: 'Success',
        description: `Tour ${getTourTypeHebrew(tour.tourType)} is now ${!tour.isActivePublic ? 'public' : 'private'}`,
      });
    } catch (error) {
      console.error('Error updating tour status:', error);
      toast({
        title: 'Error',
        description: 'Failed to update tour visibility',
        variant: 'destructive',
      });
    }
  };

  const copyToClipboard = tourId => {
    const url = `${window.location.origin}/public-tour/${tourId}`;
    navigator.clipboard.writeText(url);
    toast({
      title: 'Success',
      description: 'Link copied to clipboard',
    });
  };

  const formatDate = dateString => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  if (!tourId) {
    return (
      <Card className="bg-muted">
        <CardContent className="py-4">
          <p className="text-center text-muted-foreground">No tour selected</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      {tours.map(tour => (
        <Card key={tour.id} className="overflow-hidden bg-card">
          <CardContent className="p-0">
            {/* Tour header */}
            <div className="bg-muted p-3 flex flex-wrap items-center justify-between gap-2 border-b">
              <div className="flex items-center gap-2">
                <Badge
                  variant={tour.isActivePublic ? 'success' : 'secondary'}
                  className="whitespace-nowrap"
                >
                  {getTourTypeHebrew(tour.tourType)}
                </Badge>
                <span className="text-xs flex items-center gap-1 text-muted-foreground">
                  <Calendar className="h-3 w-3" />
                  {formatDate(tour.createdAt)}
                </span>
              </div>

              {showToggle && canEdit && (
                <div className="flex items-center space-x-2">
                  <Switch
                    id={`active-${tour.id}`}
                    checked={tour.isActivePublic}
                    onCheckedChange={() => togglePublicStatus(tour)}
                  />
                  <Label htmlFor={`active-${tour.id}`} className="text-xs">
                    {tour.isActivePublic ? labelOptions.active : labelOptions.inactive}
                  </Label>
                </div>
              )}
            </div>

            {/* Tour actions */}
            <div className="p-3 grid grid-cols-1 sm:grid-cols-4 gap-2">
              {showEditorButton && canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() =>
                    window.open(`${window.location.origin}/tour-editor/${tour.id}`, '_blank')
                  }
                >
                  <Edit className="h-4 w-4" />
                  <span className="sm:inline hidden">Tour Editor</span>
                </Button>
              )}

              <Button
                variant="outline"
                size="sm"
                className="flex items-center gap-1"
                onClick={() =>
                  window.open(`${window.location.origin}/public-tour/${tour.id}`, '_blank')
                }
              >
                <ExternalLink className="h-4 w-4" />
                <span className="sm:inline hidden">View Tour</span>
              </Button>

              {showCopyButton && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() => copyToClipboard(tour.id)}
                >
                  <Copy className="h-4 w-4" />
                  <span className="sm:inline hidden">Copy Link</span>
                </Button>
              )}

              {showEditButton && canEdit && (
                <Button
                  variant="outline"
                  size="sm"
                  className="flex items-center gap-1"
                  onClick={() => {
                    setSelectedTourForEdit(tour);
                    setEditModalOpen(true);
                  }}
                >
                  <Pencil className="h-4 w-4" />
                  <span className="sm:inline hidden">Edit Tour</span>
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      ))}

      {editModalOpen && selectedTourForEdit && (
        <EditTourModal
          isOpen={editModalOpen}
          onClose={() => {
            setEditModalOpen(false);
            setSelectedTourForEdit(null);
          }}
          selectedTour={selectedTourForEdit}
          onUpdateTourFields={() => {
            // If you want, refetch tours here after editing
            fetchReleatedTours();
            window.dispatchEvent(new Event('refetch-tours'));
          }}
        />
      )}
    </div>
  );
};

export default PublicTourLinkManager;
